import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import {
  Button,
  IconButton,
  InlineLoading,
  InlineNotification,
} from "@carbon/react";
import { Close, Renew } from "@carbon/icons-react";
import { ScreenContext } from "../../types";
import { useScreenAssistant } from "../../hooks/useScreenAssistant";
import ScreenAssistantMessages from "./ScreenAssistantMessages";
import ScreenAssistantComposer from "./ScreenAssistantComposer";
import ScreenContextChip from "./ScreenContextChip";

const EXAMPLES = ["summary", "pending", "compare"] as const;

type Props = {
  /** A bottom sheet on a phone, a side panel otherwise. */
  sheet: boolean;
  companyId: string | null | undefined;
  context: ScreenContext | null;
  contextActive: boolean;
  onContextToggle: (active: boolean) => void;
  onClose: () => void;
};

/** The chat itself: header, the screen it is about, the conversation and the question box. */
const ScreenAssistantPanel = ({
  sheet,
  companyId,
  context,
  contextActive,
  onContextToggle,
  onClose,
}: Props) => {
  const { t } = useTranslation();
  const panelRef = useRef<HTMLElement>(null);
  const chat = useScreenAssistant({ enabled: true, companyId });
  const screenContext = contextActive ? context : null;

  useEffect(() => {
    panelRef.current?.focus();
  }, []);

  const ask = (text: string, onSent?: () => void) => {
    chat.clearSendError();
    chat.send(text, { screenContext, onSent });
  };

  const body = () => {
    if (chat.isLoading) {
      return (
        <div className="screen-assistant__state">
          <InlineLoading description={t("screenAssistant.loading")} />
        </div>
      );
    }
    if (chat.isLoadError) {
      return (
        <div className="screen-assistant__state">
          <InlineNotification
            kind="error"
            lowContrast
            hideCloseButton
            title={t("screenAssistant.loadError")}
          />
          <Button kind="tertiary" size="sm" onClick={chat.retry}>
            {t("screenAssistant.retry")}
          </Button>
        </div>
      );
    }
    if (chat.messages.length === 0) {
      return (
        <div className="screen-assistant__state screen-assistant__empty">
          <p>{t("screenAssistant.empty")}</p>
          <div className="screen-assistant__examples">
            {EXAMPLES.map((example) => (
              <Button
                key={example}
                kind="ghost"
                size="sm"
                disabled={chat.isBusy}
                onClick={() => ask(t(`screenAssistant.examples.${example}`))}
              >
                {t(`screenAssistant.examples.${example}`)}
              </Button>
            ))}
          </div>
        </div>
      );
    }
    return (
      <ScreenAssistantMessages
        messages={chat.messages}
        handlers={{
          answeringActionId: chat.answeringActionId,
          onConfirm: chat.confirmAction,
          onCancel: chat.cancelAction,
        }}
      />
    );
  };

  return (
    <section
      ref={panelRef}
      tabIndex={-1}
      className={`screen-assistant__panel screen-assistant__panel--${
        sheet ? "sheet" : "side"
      }`}
      role={sheet ? "dialog" : "complementary"}
      aria-modal={sheet || undefined}
      aria-labelledby="screen-assistant-title"
      onKeyDown={(event) => {
        if (event.key === "Escape") onClose();
      }}
    >
      <header className="screen-assistant__header">
        <h2 id="screen-assistant-title" className="screen-assistant__title">
          {t("screenAssistant.title")}
        </h2>
        <IconButton
          kind="ghost"
          size="sm"
          label={t("screenAssistant.startOver")}
          disabled={!chat.canStartOver}
          onClick={chat.startOver}
        >
          <Renew />
        </IconButton>
        <IconButton
          kind="ghost"
          size="sm"
          label={t("screenAssistant.close")}
          onClick={onClose}
        >
          <Close />
        </IconButton>
      </header>
      {context && (
        <ScreenContextChip
          context={context}
          active={contextActive}
          onToggle={onContextToggle}
        />
      )}
      <div className="screen-assistant__body">{body()}</div>
      {chat.actionFailed && (
        <InlineNotification
          kind="warning"
          lowContrast
          hideCloseButton
          className="screen-assistant__send-error"
          title={t("screenAssistant.actions.answerError")}
        />
      )}
      {chat.sendErrorKey && (
        <InlineNotification
          kind="error"
          lowContrast
          className="screen-assistant__send-error"
          title={t(chat.sendErrorKey)}
          onClose={() => {
            chat.clearSendError();
            return false;
          }}
        />
      )}
      <ScreenAssistantComposer disabled={chat.isBusy} onSend={ask} />
    </section>
  );
};

export default ScreenAssistantPanel;
